import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import { BusinessPartnerBySubRoute } from '../interfaces/busines-partner-by-sub-route.interface';
import {
  Fleet,
  LatLon,
  MultidayOrder,
  PlanMultidayRequest,
  PlanOrder,
  PlanRequest,
  Shift,
  SortRequest,
  SortStop,
  Truck,
  WeekDay,
} from './route-optimization.interface';

/**
 * Fallback de coordenadas de origen para el POC, keyed por
 * `salesOffice.externalKeys.code`. Coordenadas en `[lat, lon]`.
 *
 * POC: temporal hasta poblar las coordenadas reales de cada oficina
 * (`address.geolocationPoint`). Verificado contra `cnf-sales-office`:
 * solo `1001` y `1002` tienen geo hoy; `1009` se cubre aquí.
 * Comayagua/Choluteca/Danlí quedan sin mapear a propósito.
 */
export const DEFAULT_ORIGIN_COORDS: Readonly<Record<string, readonly [number, number]>> = {
  '1001': [13.980763, -87.194663], // Ofic. Germania (Tegucigalpa)
  '1002': [15.551142, -88.005399], // Ofic. San Pedro Sula
  '1009': [14.637708, -86.218433], // Ofic. Juticalpa
};

/**
 * Una geolocalización es válida si es un par `[lat, lon]` numérico, finito,
 * dentro de rango y **sin ningún componente en 0** (ni `lat` ni `lon`).
 *
 * Un `0` exacto en latitud o longitud es el valor de un registro **sin
 * geocodificar** (Null Island / meridiano cero, frente a África, a ~9,500 km
 * de Honduras): rompe la matriz de Valhalla (supera el límite de distancia →
 * `422`). Como ningún punto real en Honduras (lat 13–16, lon −83…−89) tiene un
 * componente exactamente en 0, se trata como "sin GPS".
 */
export function isValidGeoPoint(
  point: number[] | undefined | null,
): point is [number, number] {
  if (!Array.isArray(point) || point.length !== 2) {
    return false;
  }
  const [lat, lon] = point;
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat !== 0 &&
    lon !== 0 &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
}

/**
 * Resuelve el origen (`start`) del recorrido en cascada:
 * 1. `salesOffice.address.geolocationPoint` si es un par válido.
 * 2. `DEFAULT_ORIGIN_COORDS[externalKeys.code]`.
 * 3. `undefined` → `/sort` ancla el recorrido en la primera parada.
 */
export function resolveStart(salesOffice?: SalesOfficeAttributes): LatLon | undefined {
  const geo = salesOffice?.address?.geolocationPoint;
  if (isValidGeoPoint(geo)) {
    return { lat: geo[0], lon: geo[1] };
  }

  const code = salesOffice?.externalKeys?.code;
  if (code && DEFAULT_ORIGIN_COORDS[code]) {
    const [lat, lon] = DEFAULT_ORIGIN_COORDS[code];
    return { lat, lon };
  }

  return undefined;
}

/**
 * Transforma los clientes de una sub-ruta (con su `geolocationPoint`) y la
 * sales-office (origen) en el `SortRequest` que espera `POST /sort`.
 *
 * Convención de coordenadas: `geolocationPoint[0] = lat`, `[1] = lon`.
 * Los clientes sin `geolocationPoint` válido se excluyen.
 *
 * Función pura: no realiza ninguna petición HTTP.
 */
export function toSortRequest(
  clients: BusinessPartnerBySubRoute[],
  salesOffice?: SalesOfficeAttributes,
): SortRequest {
  const stops: SortStop[] = (clients ?? [])
    .filter((client) => isValidGeoPoint(client?.principalAddress?.geolocationPoint))
    .map((client) => {
      const point = client.principalAddress.geolocationPoint;
      return { id: client._id, lat: point[0], lon: point[1], service_min: SERVICE_MIN_PER_STOP };
    });

  const start = resolveStart(salesOffice);

  return start ? { stops, start } : { stops };
}

/* ------------------------------------------------------------------ *
 *  Multi-día (/plan-multiday)                                          *
 * ------------------------------------------------------------------ */

/** Frecuencia de un cliente dentro de la sub-ruta (forma mínima usada aquí). */
interface ClientFrequency {
  _id?: string;
  type?: string;
  days?: number[];
}

/** Mapeo del código de día del sistema (1=Lunes … 7=Domingo) a `WeekDay`. */
export const DAY_CODE_TO_WEEKDAY: Readonly<Record<number, WeekDay>> = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
  7: 'sun',
};

/**
 * Días elegibles de un cliente (`WeekDay[]`), tomados de sus frecuencias `W`.
 * Si se pasa `allowedDays` (códigos 1..7), solo cuenta los días del cliente que
 * caen en ese conjunto (intersección); si no, la unión de todos sus días.
 * Las frecuencias `C` (por fechas) no aportan día de semana.
 */
export function clientDeliveryDays(
  client: BusinessPartnerBySubRoute,
  allowedDays?: number[],
): WeekDay[] {
  const freqs = (client?.frequenciesFromThisSubRoute ?? []) as unknown as ClientFrequency[];
  const codes = new Set<number>();
  for (const f of freqs) {
    if (f?.type !== 'W') continue;
    for (const d of f?.days ?? []) {
      if (!DAY_CODE_TO_WEEKDAY[d]) continue;
      if (allowedDays && !allowedDays.includes(d)) continue;
      codes.add(d);
    }
  }
  return [...codes].sort((a, b) => a - b).map((d) => DAY_CODE_TO_WEEKDAY[d]);
}

/** Opciones de la planificación multi-día. */
export interface PlanMultidayOptions {
  /** `YYYY-MM-DD` de inicio del horizonte. */
  startDate: string;
  /** Tamaño del horizonte en días (default 7). */
  days?: number;
  /** Limitar los días elegibles a este conjunto (códigos 1..7). */
  allowedDays?: number[];
  /** Franja diurna del recorrido (default 08:00–17:00). */
  shift?: { start: string; end: string };
  /**
   * Días elegibles para clientes **sin frecuencia propia** (sin asignar): el
   * optimizador les propone uno de estos días. Normalmente los días que ya
   * trabaja la sub-ruta.
   */
  fallbackDays?: WeekDay[];
  /** Número de camiones idénticos a dimensionar (default 1). */
  truckCount?: number;
  /**
   * Días fijados a mano (`clientId → día`), para imponer una asignación que el
   * optimizador no eligió.
   *
   * Se expresa reduciendo `delivery_days` del cliente **al día fijado**: es la
   * semántica que el contrato ya tiene (*"absent means any day"*, una lista
   * explícita restringe), así que no hace falta tocar `POST /plan-multiday`.
   *
   * Se aplica **antes** del filtro de días elegibles, de modo que un cliente sin
   * ningún día propio —que hoy se cae del request y por eso queda
   * `no_eligible_day`— entre con su día fijado. Y gana sobre `allowedDays`:
   * es una decisión manual explícita, no una preferencia.
   */
  pinnedDays?: ReadonlyMap<string, WeekDay>;
  /**
   * Clientes **desplazados** de un día (`clientId → día del que sale`), la otra
   * mitad de una sustitución.
   *
   * Se les **quita** ese día de sus elegibles, conservando los demás para que el
   * motor pueda recolocarlos. Si no les queda ninguno, la orden no viaja: el
   * contrato rechaza `delivery_days: []` con `422`, y quedar fuera es
   * exactamente lo que el usuario decidió al desplazarlos.
   *
   * Una fijación sobre el mismo cliente gana: es la decisión más reciente.
   */
  displacedDays?: ReadonlyMap<string, WeekDay>;
  /**
   * Acota el horizonte a un rango de fechas (`YYYY-MM-DD`, ambos inclusive).
   *
   * Permite recalcular **solo los días afectados** por un cambio manual en vez
   * de la semana entera: medido, un día cuesta ~1,2 s contra ~3,2 s de tres. Es
   * exacto porque el planificador es greedy hacia adelante —nunca revisa un día
   * ya resuelto— y el motor es determinista.
   */
  dateRange?: { start: string; end: string };
}

/** Días entre dos fechas `YYYY-MM-DD`, ambas inclusive (mínimo 1). */
function daysBetween(start: string, end: string): number {
  const from = Date.parse(`${start}T00:00:00`);
  const to = Date.parse(`${end}T00:00:00`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 1;
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1);
}

/** Capacidad "infinita" para preventa (la capacidad no es la restricción). */
const UNLIMITED_CAPACITY = 1_000_000_000;
const NOMINAL_WEIGHT = 1;

/**
 * Minutos de servicio (atención) por parada. Evita que paradas muy cercanas
 * (tramo ~0 min) colapsen sus ETAs en el mismo minuto.
 */
export const SERVICE_MIN_PER_STOP = 15;

/**
 * Peso por defecto (> 0) cuando la orden no tiene líneas/peso. El motor exige
 * `weight > 0`; muchas órdenes de entrega aún no traen `detail` con productos,
 * así que se usa este piso para que el ruteo corra (capacidad no significativa
 * hasta que se pueble el peso real).
 */
export const DEFAULT_ORDER_WEIGHT = 1;

/**
 * Transforma los clientes de una sub-ruta en un `PlanMultidayRequest`: un solo
 * camión (el vendedor) que trabaja los días de la frecuencia, y cada cliente es
 * una orden elegible en sus días (`delivery_days`); el motor decide el día.
 *
 * Función pura: no realiza ninguna petición HTTP. Filtra clientes sin
 * `geolocationPoint` válido y sin días elegibles.
 */
export function toPlanMultidayRequest(
  clients: BusinessPartnerBySubRoute[],
  salesOffice: SalesOfficeAttributes | undefined,
  options: PlanMultidayOptions,
): PlanMultidayRequest {
  const shift = options.shift ?? { start: '08:00', end: '17:00' };

  const orders: MultidayOrder[] = (clients ?? [])
    .filter((c) => isValidGeoPoint(c?.principalAddress?.geolocationPoint))
    .map((c) => {
      const point = c.principalAddress.geolocationPoint;
      const pinned = options.pinnedDays?.get(c._id);
      const displaced = options.displacedDays?.get(c._id);
      const own = clientDeliveryDays(c, options.allowedDays);
      // Fijado a mano → ese día y solo ese (gana sobre todo lo demás).
      // Desplazado → sus días menos aquel del que salió.
      // Si no, su frecuencia propia; y sin frecuencia (sin asignar) → los días
      // de la sub-ruta.
      const fallback = own.length ? own : (options.fallbackDays ?? []);
      const deliveryDays = pinned
        ? [pinned]
        : displaced
          ? fallback.filter((d) => d !== displaced)
          : fallback;
      return {
        id: c._id,
        customer_id: c._id,
        lat: point[0],
        lon: point[1],
        weight: NOMINAL_WEIGHT,
        service_min: SERVICE_MIN_PER_STOP,
        delivery_days: deliveryDays,
      };
    })
    .filter((o) => (o.delivery_days?.length ?? 0) > 0);

  // Días de trabajo del vendedor = unión de los días elegibles de las órdenes.
  const workingDays = [...new Set(orders.flatMap((o) => o.delivery_days ?? []))];

  // El warehouse es obligatorio en /plan-multiday: origen de la oficina o, si
  // no hay, la primera parada como ancla.
  const start = resolveStart(salesOffice);
  const warehouse: LatLon =
    start ?? (orders.length ? { lat: orders[0].lat, lon: orders[0].lon } : { lat: 0, lon: 0 });

  const truckCount = Math.max(1, Math.floor(options.truckCount ?? 1));
  const trucks = Array.from({ length: truckCount }, (_unused, i) => ({
    id: `VENDEDOR-${i + 1}`,
    capacity: UNLIMITED_CAPACITY,
    shift,
    working_days: workingDays,
  }));

  const range = options.dateRange;
  return {
    horizon: range
      ? { start_date: range.start, days: daysBetween(range.start, range.end) }
      : { start_date: options.startDate, days: options.days ?? 7 },
    warehouse,
    trucks,
    orders,
  };
}

/* ------------------------------------------------------------------ *
 *  /plan — asignación de flota de un día (rutas de entrega)            *
 * ------------------------------------------------------------------ */

/** Opciones de `toPlanRequest`. Exactamente uno de `trucks` o `fleet`. */
export interface PlanOptions {
  /** Etiqueta de la fecha del plan. */
  date: string;
  /** Flota explícita (modo `trucks`). */
  trucks?: Truck[];
  /** Dimensionamiento (modo `fleet`). */
  fleet?: Fleet;
  /** Origen de los camiones; si falta se resuelve por la oficina o la 1ª orden. */
  warehouse?: LatLon;
  salesOffice?: SalesOfficeAttributes;
  /** Unidad de `weight`/capacidad (`'lb'` por defecto en Cortijo). */
  weightUnit?: 'kg' | 'lb';
  /** Modo de cálculo de peso del proyecto (`BY_DIMENSIONS` / `BY_QUANTITY`). */
  loadCalculationMode?: string;
  /** Minutos de atención por parada (default `SERVICE_MIN_PER_STOP`). */
  serviceMin?: number;
  /**
   * Si `true`, agrupa varias órdenes del **mismo cliente** en una sola parada
   * (suma el peso). Evita visitar el mismo punto varias veces.
   */
  groupByCustomer?: boolean;
}

/** Coordenada de entrega de una orden (`order.address.geolocationPoint = [lat, lon]`). */
function orderGeoPoint(order: OrderAttributes): number[] | undefined {
  return order?.address?.geolocationPoint;
}

/** Línea de detalle con cantidad y peso/unidad del material. */
interface WeightedDetailLine {
  baseQuantity?: number;
  material?: {
    dimensions?: { grossWeight?: number; weightUnitOfMeasureCode?: string };
  };
}

/**
 * Peso REAL de entrega de una orden, en **libras**: por cada línea
 * `baseQuantity × material.dimensions.grossWeight` (peso por unidad). Convierte a
 * lb si el material está en KG. Esto sí es comparable con la capacidad del
 * camión (`grossWeight` en lb) — a diferencia de `calculateTotalWeight`, que en
 * modo BY_BASE_QUANTITY solo suma cantidades (no multiplica por el peso/unidad).
 */
export function orderDeliveryWeightLb(order: OrderAttributes): number {
  const detail = (Array.isArray(order?.detail) ? order.detail : []) as WeightedDetailLine[];
  let lb = 0;
  for (const line of detail) {
    const qty = line?.baseQuantity ?? 0;
    const dim = line?.material?.dimensions;
    const perUnit = dim?.grossWeight ?? 0;
    const code = (dim?.weightUnitOfMeasureCode ?? 'LB').trim().toUpperCase();
    const perUnitLb = code === 'KG' ? perUnit * 2.20462 : perUnit;
    lb += qty * perUnitLb;
  }
  return lb;
}

/**
 * Transforma órdenes de entrega (`DT003`) en un `PlanRequest`: cada orden es
 * una `PlanOrder` con coords de `order.address.geolocationPoint` y `weight` por
 * `calculateTotalWeight`. Excluye órdenes sin coordenadas válidas. Arma `trucks`
 * o `fleet` según las opciones; el `warehouse` sale del origen dado / la oficina
 * / la primera orden.
 *
 * Función pura: no realiza ninguna petición HTTP.
 */
/** Una orden que no llega al motor, con el motivo (nunca se descarta en silencio). */
export interface ExcludedPlanOrder {
  order: OrderAttributes;
  reason: 'invalid_geo' | 'missing_identity';
}

/**
 * Identidad de una orden para el contrato de `/plan`: su id y el del cliente.
 *
 * Vive acá y no duplicada en cada consumidor porque `/plan` exige **ambos no
 * vacíos** (`min_length=1`) y responde `422` si falta cualquiera. Una sola orden
 * sin `_id` o sin `shipTo` bastaba para tumbar la petición completa de su grupo,
 * así que quien decide qué es planificable tiene que usar exactamente este
 * criterio.
 */
export function planOrderIdentity(order: OrderAttributes): { id: string; customerId: string } {
  const id = (order as { _id?: { toString(): string } })?._id?.toString() ?? '';
  const shipTo = order?.shipTo as { _id?: { toString(): string }; id?: unknown } | undefined;
  const customerId = shipTo?._id?.toString() ?? String(shipTo?.id ?? '');
  return { id, customerId };
}

/** ¿La orden tiene la identidad que el contrato exige? */
export function hasPlanIdentity(order: OrderAttributes): boolean {
  const { id, customerId } = planOrderIdentity(order);
  return id.length > 0 && customerId.length > 0;
}

/**
 * Convierte órdenes `DT003` en `PlanOrder`, apartando las que no tienen
 * coordenadas válidas en vez de filtrarlas en silencio.
 *
 * Compartido por `toPlanRequest` (una request por oficina) y
 * `toGroupPlanRequests` (una por grupo) para que las dos rutas produzcan
 * exactamente las mismas órdenes: identidad, cliente, coordenadas y el peso
 * real en libras con su piso por defecto.
 */
function toPlanOrders(
  orders: OrderAttributes[],
  serviceMin: number,
): { planOrders: PlanOrder[]; excluded: ExcludedPlanOrder[] } {
  const planOrders: PlanOrder[] = [];
  const excluded: ExcludedPlanOrder[] = [];
  for (const o of orders ?? []) {
    if (!isValidGeoPoint(orderGeoPoint(o))) {
      excluded.push({ order: o, reason: 'invalid_geo' });
      continue;
    }
    const { id, customerId } = planOrderIdentity(o);
    if (!id || !customerId) {
      // Sin id o sin cliente el contrato responde `422` y se pierde el grupo
      // entero por una sola orden. Se aparta acá, como las que no tienen geo.
      excluded.push({ order: o, reason: 'missing_identity' });
      continue;
    }
    const point = orderGeoPoint(o) as [number, number];
    const weight = orderDeliveryWeightLb(o);
    planOrders.push({
      id,
      customer_id: customerId,
      lat: point[0],
      lon: point[1],
      weight: Number.isFinite(weight) && weight > 0 ? weight : DEFAULT_ORDER_WEIGHT,
      service_min: serviceMin,
    });
  }
  return { planOrders, excluded };
}

export function toPlanRequest(orders: OrderAttributes[], options: PlanOptions): PlanRequest {
  const serviceMin = options.serviceMin ?? SERVICE_MIN_PER_STOP;

  const { planOrders } = toPlanOrders(orders, serviceMin);

  // Una parada por cliente: agrupa órdenes del mismo `customer_id` sumando peso.
  // Si un cliente excede la capacidad de un camión, se divide en varias paradas.
  // El tope es el camión MÁS GRANDE disponible (en mixto, el mayor de los tipos).
  const fleetCapacities = [
    ...(options.fleet?.truck_type ? [options.fleet.truck_type.capacity] : []),
    ...(options.fleet?.truck_types ?? []).map((t) => t.truck_type.capacity),
    ...(options.trucks ?? []).map((t) => t.capacity),
  ];
  const maxStopWeight = fleetCapacities.length ? Math.max(...fleetCapacities) : undefined;
  const finalOrders = options.groupByCustomer
    ? groupOrdersByCustomer(planOrders, maxStopWeight)
    : planOrders;

  const start = resolveStart(options.salesOffice);
  const warehouse: LatLon =
    options.warehouse ??
    start ??
    (finalOrders.length ? { lat: finalOrders[0].lat, lon: finalOrders[0].lon } : { lat: 0, lon: 0 });

  const request: PlanRequest = {
    date: options.date,
    warehouse,
    orders: finalOrders,
    weight_unit: options.weightUnit ?? 'lb',
  };
  if (options.fleet) {
    request.fleet = options.fleet;
  } else {
    request.trucks = options.trucks ?? [];
  }
  return request;
}

/* ------------------------------------------------------------------ *
 *  /plan por grupo de planificación (una request por sub-ruta)         *
 * ------------------------------------------------------------------ */

/** Un grupo listo para mapearse: sus órdenes y la flota ya resuelta. */
export interface PlanGroupInput {
  /** Clave única del grupo dentro de la corrida. */
  key: string;
  orders: OrderAttributes[];
  /**
   * La flota REAL del grupo: los camiones que el maestro asigna a su territorio.
   *
   * Era un único `truck`, que se inventaba cuando el maestro no lo declaraba —de
   * ahí salían las rutas con camiones que no existen—. Ahora puede traer varios
   * (un territorio con dos camiones es UN problema con dos vehículos, no dos
   * problemas) y puede venir **vacío**: sin flota no hay petición que emitir.
   */
  trucks: Truck[];
  /** Ventana de entrega a aplicar a las órdenes del grupo, si la hay. */
  window?: Shift;
}

/** Opciones comunes a todos los grupos de una oficina. */
export interface GroupPlanOptions {
  date: string;
  warehouse?: LatLon;
  salesOffice?: SalesOfficeAttributes;
  weightUnit?: 'kg' | 'lb';
  serviceMin?: number;
  /** Una parada por cliente (varias órdenes del mismo cliente = 1 visita). */
  groupByCustomer?: boolean;
}

/** La request de un grupo, con lo que quedó fuera de ella. */
export interface GroupPlanRequest {
  key: string;
  request: PlanRequest;
  excluded: ExcludedPlanOrder[];
}

/**
 * Construye **una `PlanRequest` por grupo de planificación** (un territorio).
 *
 * Por qué no una sola con todas las órdenes: el costo del motor es superlineal
 * en el tamaño del problema, y el territorio ya acota el problema con la flota
 * que puede servirlo (ver `plan-partitioning.partitionByTerritory`). Cada request
 * va en modo `trucks` con **la flota real de ese territorio** en vez de un
 * `fleet` genérico: así el motor no redescubre una asignación que ya existe, y el
 * `unassigned` de cada grupo pasa a significar "a este territorio le falta
 * capacidad", que es la pregunta que el negocio realmente hace.
 *
 * Un grupo **sin flota no produce request**. Antes era imposible —siempre había
 * un camión, aunque hubiera que inventarlo—, y era exactamente el bug: 21 grupos
 * producían 21 rutas con 2 camiones asignados. Un grupo sin camión no es un
 * problema con capacidad por defecto, es un pedido que hoy no sale.
 *
 * El `warehouse` es el de la oficina y se resuelve una vez para todos los
 * grupos. Las órdenes sin coordenadas válidas no viajan y se devuelven en
 * `excluded`, para que la pantalla pueda reconciliar los totales.
 *
 * Función pura: no realiza ninguna petición HTTP.
 */
export function toGroupPlanRequests(
  groups: PlanGroupInput[],
  options: GroupPlanOptions,
): GroupPlanRequest[] {
  const serviceMin = options.serviceMin ?? SERVICE_MIN_PER_STOP;
  const start = resolveStart(options.salesOffice);
  const fallbackWarehouse = options.warehouse ?? start;

  return (groups ?? []).filter((group) => (group?.trucks ?? []).length > 0).map((group) => {
    const { planOrders, excluded } = toPlanOrders(group.orders, serviceMin);

    // Una parada por cliente; el tope de división es la capacidad del camión MÁS
    // GRANDE del territorio: partir por el más chico dejaría fuera visitas que el
    // grande sí puede llevar.
    const maxCapacity = Math.max(...group.trucks.map((t) => t.capacity));
    const grouped = options.groupByCustomer
      ? groupOrdersByCustomer(planOrders, maxCapacity)
      : planOrders;
    // Regla de negocio de esta pantalla: NO hay entrega parcial. Sin esto, el
    // servicio divide el pedido sobredimensionado en sub-órdenes (`#N`) y el
    // plan entrega una mitad — el operador veía al mismo cliente asignado Y
    // rechazado a la vez. Indivisible, vuelve entero como `exceeds_capacity`,
    // visible hasta que se reenvíe en pedidos que quepan (o con camión mayor).
    const finalOrders = grouped.map((o) => ({
      ...o,
      splittable: false as const,
      ...(group.window ? { window: group.window } : {}),
    }));

    const warehouse: LatLon =
      fallbackWarehouse ??
      (finalOrders.length ? { lat: finalOrders[0].lat, lon: finalOrders[0].lon } : { lat: 0, lon: 0 });

    return {
      key: group.key,
      request: {
        date: options.date,
        warehouse,
        orders: finalOrders,
        weight_unit: options.weightUnit ?? 'lb',
        trucks: group.trucks,
      },
      excluded,
    };
  });
}

/** Separador de las paradas divididas de un mismo cliente, del lado del front. */
const SPLIT_SEP = '__s';

/**
 * Separador de las sub-órdenes que divide el SERVICIO.
 *
 * Cuando una orden sola supera la capacidad del camión más grande, `/plan` la parte
 * en `<id>#1`, `#2`… antes de armar el problema (`split.py`, `SPLIT_DELIMITER`). Son
 * dos convenciones distintas para lo mismo, y el front no conocía la del servicio:
 * un id `...#2` no resolvía su orden base, así que la parada aparecía con `0 lb`
 * — un peso inventado por no encontrar el dato, no un peso real.
 */
const SERVICE_SPLIT_SEP = '#';

/**
 * Id base de una parada, sin ningún sufijo de división.
 *
 * Quita los de las dos convenciones —`__sN` del front y `#N` del servicio— y en
 * cualquier orden: una orden puede venir dividida por capacidad de cliente en el
 * front y vuelta a dividir por el servicio.
 */
export function basePlanOrderId(id: string): string {
  let base = id;
  for (const sep of [SPLIT_SEP, SERVICE_SPLIT_SEP]) {
    const i = base.indexOf(sep);
    if (i >= 0) base = base.slice(0, i);
  }
  return base;
}

/**
 * Registra en `weightByOrderId` el peso de las sub-órdenes `#N` que dividió el
 * SERVICIO (ver `SERVICE_SPLIT_SEP`).
 *
 * El request nunca las contiene —nacen en `split.py` cuando una parada supera la
 * capacidad del camión— así que su peso no está en ningún índice del front y la
 * vista caía al total del cliente: una parte de 5,400 lb se mostraba de 10,800 y
 * la barra de capacidad aparentaba un camión sobrecargado que no existe.
 *
 * La derivación es espejo de la matemática de `split.py`: partes IGUALES
 * redondeadas a 3 decimales, y la última absorbe el residuo para que la suma sea
 * exactamente el peso enviado. El número de partes sale del mayor índice `#N`
 * observado en la respuesta (asignadas + no asignadas): el servicio devuelve
 * todas, en una lista o en la otra.
 *
 * Muta el mapa recibido a propósito: es el índice acumulado de la corrida.
 */
export function registerServiceSplitWeights(
  planOrderIds: Iterable<string>,
  weightByOrderId: Map<string, number>,
): void {
  const partsByRequestId = new Map<string, number>();
  for (const id of planOrderIds) {
    const at = id.indexOf(SERVICE_SPLIT_SEP);
    if (at < 0) continue;
    const requestId = id.slice(0, at);
    const part = Number(id.slice(at + SERVICE_SPLIT_SEP.length));
    if (!Number.isInteger(part) || part < 1) continue;
    partsByRequestId.set(requestId, Math.max(partsByRequestId.get(requestId) ?? 0, part));
  }
  for (const [requestId, parts] of partsByRequestId) {
    const total = weightByOrderId.get(requestId);
    if (total === undefined) continue;
    const base = Math.round((total / parts) * 1000) / 1000;
    for (let p = 1; p <= parts; p++) {
      const weight = p < parts ? base : Math.round((total - base * (parts - 1)) * 1000) / 1000;
      weightByOrderId.set(`${requestId}${SERVICE_SPLIT_SEP}${p}`, weight);
    }
  }
}

/**
 * Agrupa varias `PlanOrder` del mismo `customer_id` en una sola parada (suma el
 * peso). Si se da `maxStopWeight` y el total del cliente lo excede, **divide** al
 * cliente en varias paradas (bin-packing) cada una ≤ `maxStopWeight`, para que
 * puedan repartirse entre camiones (una parada es indivisible). Las paradas
 * extra llevan id `baseId__sN` (resoluble con `basePlanOrderId`).
 */
export function groupOrdersByCustomer(orders: PlanOrder[], maxStopWeight?: number): PlanOrder[] {
  const byCustomer = new Map<string, PlanOrder[]>();
  for (const o of orders) {
    const key = o.customer_id || o.id;
    const list = byCustomer.get(key);
    if (list) list.push(o);
    else byCustomer.set(key, [o]);
  }

  const result: PlanOrder[] = [];
  for (const list of byCustomer.values()) {
    const total = list.reduce((s, o) => s + o.weight, 0);
    const base = list[0];

    if (!maxStopWeight || maxStopWeight <= 0 || total <= maxStopWeight) {
      result.push({ ...base, weight: total });
      continue;
    }

    // Bin-packing: empaca las órdenes del cliente en paradas ≤ capacidad.
    const bins: number[] = [];
    const sorted = [...list].sort((a, b) => b.weight - a.weight);
    for (const o of sorted) {
      let placed = false;
      for (let i = 0; i < bins.length; i++) {
        if (bins[i] + o.weight <= maxStopWeight) {
          bins[i] += o.weight;
          placed = true;
          break;
        }
      }
      if (!placed) bins.push(o.weight); // nueva parada (si una orden sola excede, queda sobredimensionada)
    }
    bins.forEach((w, idx) => {
      result.push({ ...base, id: idx === 0 ? base.id : `${base.id}${SPLIT_SEP}${idx}`, weight: w });
    });
  }
  return result;
}
